import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useInnerHeroImage } from '../hooks/useInnerHeroImage';
import {
  INNER_HERO_SUBTITLE_CLASS,
  INNER_HERO_TITLE_CLASS,
  InnerHeroBackground,
  InnerHeroShell,
} from '../ui/InnerHeroShell';

/**
 * Contact page hero. Keeps the `contact-hero.*` keys the shared PageHero
 * instance saved to, so existing edits carry over.
 */

const TITLE = '<p>Contact Us</p>';
const SUBTITLE = '<p>Questions? Our Leasing Team Is Ready to Help</p>';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Contact Us banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A strong photo of the property exterior, main entrance, leasing office, or community grounds — something that says "this is where to find us". Alt text describes the chosen photo.',
    context:
      'Full-bleed inner page hero banner behind the "Contact Us" title, under a dark overlay; crops to a wide landscape strip. Must be sharp, well lit and uncluttered — never a map, logo, or graphic. Use a photo not already used by the homepage hero or the other inner page heroes (Floor Plans, Amenities, Gallery, Neighborhood, Virtual Tour); Gallery may also use an exterior, so pick a different angle or shot. Reuse a photo only if no unused exterior or community photo fits.',
  },
};

export function ContactHero() {
  const bg = useInnerHeroImage('contact-hero.bg_image', bgImageDef, 'contact', {
    altText: 'Contact Us banner',
  });

  return (
    <InnerHeroShell
      background={
        <EditableSlotGroup
          groupPath="contact-hero.bg_image"
          definition={bgImageDef}
          className="absolute inset-0"
        >
          <InnerHeroBackground image={bg.image} alt={bg.alt} />
        </EditableSlotGroup>
      }
    >
      <RichTextContent
        path="contact-hero.subtitle"
        label="Subtitle"
        defaultValue={SUBTITLE}
        as="h2"
        className={INNER_HERO_SUBTITLE_CLASS}
      />
      <RichTextContent
        path="contact-hero.title"
        label="Title"
        defaultValue={TITLE}
        as="h1"
        className={INNER_HERO_TITLE_CLASS}
      />
    </InnerHeroShell>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactHero',
  displayName: 'Contact Hero',
  tokens: {
    'contact-hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Questions? Our Leasing Team Is Ready to Help</p>',
    },
    'contact-hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Contact Us</p>',
    },
  },
  slotGroups: {
    'contact-hero.bg_image': bgImageDef,
  },
};

(ContactHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactHero;
